'use client';

import { useSession } from '@shared/auth';
import { getSiteConfig } from '@shared/config';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useEffectEvent } from 'react';
import { formatProfileMobileNumberForDisplay } from '../../form-controls/PhoneNumberInput/phoneNumberUtils';
import { AccountReviewReminderSheet } from './components/account-review-reminder/AccountReviewReminderSheet';
import { AccountAccessSection } from './components/AccountAccessSection';
import { AccountReviewSection } from './components/AccountReviewSection';
import { ChannelsEditSheet } from './components/ChannelsEditSheet';
import { CloseAccountSheet } from './components/close-account/components/CloseAccountSheet';
import { CloseAccountSection } from './components/CloseAccountSection';
import { ContactPreferencesEditSheet } from './components/ContactPreferencesEditSheet';
import { ContactPreferencesSection } from './components/ContactPreferencesSection';
import { Darkmode } from './components/DarkMode/Darkmode';
import { EditEmailSheet } from './components/edit-email/components/EditEmailSheet';
import { EditMobileNumberSheet } from './components/edit-mobile-number/components/EditMobileNumberSheet';
import { SettingsLoadError } from './components/SettingsLoadError.tsx';
// import { SessionsDevicesSection } from './components/SessionsDevicesSection';
import { useAccountReviewReminderData } from './hooks/useAccountReviewReminderData';
import { useContactPreferencesData } from './hooks/useContactPreferencesData';
import { useSettingsSheets } from './hooks/useSettingsSheets';
import { useVerificationStatus } from './hooks/useVerificationStatus';
import { mapChannelsToApiKeys } from './utils/channelsMapper';
import { mapProductPreferencesToApiKeys } from './utils/contactPreferencesMapper';

const SETTINGS_ACTION_VERIFY_EMAIL = 'verify-email';
const SETTINGS_ACTION_VERIFY_PHONE = 'verify-phone';

/** Player profile — mobile fields are national digits, dial code, and ISO country (no E.164 in `mobilePhoneNumber`). */
type PlayerProfile = {
  email?: string;
  country?: string;
  mobilePhoneNumber?: string;
  countryCode?: string;
};

/** True when profile has both national number and dial code (after trim). */
function hasStoredMobileNumber(profile: PlayerProfile | null | undefined): boolean {
  return Boolean(profile?.mobilePhoneNumber?.trim() && profile?.countryCode?.trim());
}

/** E.164 from profile `countryCode` + `mobilePhoneNumber` for sheets and close account. */
function buildMobileNumberFromProfile(profile: PlayerProfile | null | undefined): string {
  const nationalDigits = profile?.mobilePhoneNumber ?? '';
  const dial = profile?.countryCode ?? '';
  if (nationalDigits && dial) {
    return `+${dial}${nationalDigits}`;
  }
  return '';
}

type SettingsProps = {
  namespace: string;
  className?: string;
};

function Settings({ namespace, className }: SettingsProps) {
  const { session } = useSession();
  const playerProfile = session?.profile as PlayerProfile | null | undefined;
  const sheets = useSettingsSheets();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { replace } = useRouter();
  const { showsAccountReviewReminder } = getSiteConfig().MY_ACCOUNT_SETTINGS;
  const {
    productPreference,
    channels,
    productSelection,
    setProductSelection,
    channelSelection,
    setChannelSelection,
    isError: isContactPreferencesError,
    isFetching: isContactPreferencesFetching,
    refetch: refetchContactPreferences,
  } = useContactPreferencesData({
    namespace,
    fallbackProductPreference: ['Lotteries', 'Casino', 'Live Casino'],
    fallbackChannels: ['Email', 'SMS'],
    isProductPreferencesSheetOpen: sheets.showContactPreferencesSheet,
    isChannelsSheetOpen: sheets.showChannelsSheet,
  });
  const { interval: currentInterval, displayLabel: accountReviewReminderLabel } = useAccountReviewReminderData(namespace);

  const replaceParams = useEffectEvent(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('action');

    replace(newParams.size ? `${pathname}?${newParams}` : pathname);
  });

  useEffect(() => {
    const action = searchParams.get('action');

    if (action === SETTINGS_ACTION_VERIFY_EMAIL) {
      sheets.setShowEditEmailSheet(true, 'verify');
    } else if (action === SETTINGS_ACTION_VERIFY_PHONE) {
      sheets.setShowEditMobileNumberSheet(true, hasStoredMobileNumber(playerProfile) ? 'verify' : 'add');
    }

    if (action) {
      replaceParams();
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const contactPreferences = {
    verticals: mapProductPreferencesToApiKeys(productPreference),
    channels: mapChannelsToApiKeys(channels),
  };

  const {
    isEmailVerified,
    isMobileVerified,
    isLoading: isVerificationLoading,
    isFetching: isVerificationFetching,
    isError: isVerificationError,
    refetch: refetchVerificationStatus,
  } = useVerificationStatus();

  const isLoading = session.status === 'loading' || isVerificationLoading || isVerificationFetching || isContactPreferencesFetching || !playerProfile;
  const hasLoadError = isVerificationError || isContactPreferencesError;
  const handleRetry = () => {
    refetchVerificationStatus();
    refetchContactPreferences();
  };
  const email = playerProfile?.email;
  const mobileNumber = buildMobileNumberFromProfile(playerProfile);
  const mobileNumberDisplay = formatProfileMobileNumberForDisplay(mobileNumber);
  const hasMobileNumber = hasStoredMobileNumber(playerProfile);

  return (
    <div className="flex flex-col gap-6" data-testid="settings-page">
      {!isLoading && hasLoadError && <SettingsLoadError namespace={namespace} onRetry={handleRetry} />}

      <AccountAccessSection
        namespace={namespace}
        className={className}
        user={{ email, mobileNumber: mobileNumberDisplay, hasMobileNumber, isLoading, isEmailVerified, isMobileVerified }}
        onEditMobile={() => sheets.setShowEditMobileNumberSheet(true, hasMobileNumber ? undefined : 'add')}
        onEditEmail={() => sheets.setShowEditEmailSheet(true)}
        onVerifyMobile={() => sheets.setShowEditMobileNumberSheet(true, 'verify')}
        onVerifyEmail={() => sheets.setShowEditEmailSheet(true, 'verify')}
      />

      {/* TODO: uncomment this when we have the sessions and devices section (JIRA ticket: CHR-2559) */}
      {/* <SessionsDevicesSection namespace={namespace} className={className} /> */}

      <ContactPreferencesSection
        namespace={namespace}
        className={className}
        productPreference={productPreference}
        channels={channels}
        onEditProductPreferences={() => sheets.setShowContactPreferencesSheet(true)}
        onEditChannels={() => {
          sheets.setShowChannelsSheet(true);
        }}
      />

      {showsAccountReviewReminder && (
        <AccountReviewSection
          namespace={namespace}
          className={className}
          accountReviewReminder={accountReviewReminderLabel}
          accountReviewReminderInterval={currentInterval}
          onEdit={() => sheets.setShowAccountReviewReminderSheet(true)}
        />
      )}

      <Darkmode />

      <CloseAccountSection namespace={namespace} className={className} onCloseAccount={() => sheets.setShowCloseAccountSheet(true)} />

      <CloseAccountSheet open={sheets.showCloseAccountSheet} onOpenChange={sheets.setShowCloseAccountSheet} />
      <EditEmailSheet open={sheets.showEditEmailSheet} onOpenChange={open => sheets.setShowEditEmailSheet(open)} user={{ email }} startOnVerifyStep={sheets.emailStartOnVerify} />
      <EditMobileNumberSheet
        open={sheets.showEditMobileNumberSheet}
        onOpenChange={open => sheets.setShowEditMobileNumberSheet(open)}
        user={{
          email: email ?? '',
          country: playerProfile?.country,
          countryCode: playerProfile?.countryCode,
          mobilePhoneNumber: playerProfile?.mobilePhoneNumber,
        }}
        namespace={`${namespace}.edit_mobile_number`}
        initialFlow={sheets.mobileInitialFlow}
      />
      <ContactPreferencesEditSheet
        open={sheets.showContactPreferencesSheet}
        onOpenChange={sheets.setShowContactPreferencesSheet}
        contactPreferences={contactPreferences}
        productSelection={productSelection}
        onProductSelectionChange={setProductSelection}
      />
      <ChannelsEditSheet
        open={sheets.showChannelsSheet}
        onOpenChange={sheets.setShowChannelsSheet}
        contactPreferences={contactPreferences}
        channelSelection={channelSelection}
        onChannelSelectionChange={setChannelSelection}
      />
      {showsAccountReviewReminder && (
        <AccountReviewReminderSheet open={sheets.showAccountReviewReminderSheet} onOpenChange={sheets.setShowAccountReviewReminderSheet} currentInterval={currentInterval} />
      )}
    </div>
  );
}

export { Settings };
